import { useState } from "react";
import {
  Labeled,
  FunctionField,
  Confirm,
  useRecordContext,
  useResourceContext,
  useGetOne,
  useDelete,
} from "react-admin";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Box,
  Rating,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { CustomerNameField } from "../../components";

const metaRatings = {
  sparseFields: [
    "id",
    "rating",
    {
      ratings: ["id", "clientId", "providerId", "createdTimestamp", "rating"],
    },
  ],
};

const metaReviews = {
  sparseFields: [
    "id",
    {
      reviews: [
        "id",
        "clientId",
        "clientUserName",
        "createdTimestamp",
        "providerId",
        "review",
      ],
    },
  ],
};

const EditRatingsReviews = () => {
  const [deleteDialogOpenFor, setDeleteDialogOpenFor] = useState(null);
  const record = useRecordContext();
  const resource = useResourceContext();
  const id = record.id;

  const {
    data: providerRatings,
    isLoading: isLoadingRatings,
    refetch: refetchRatings,
    error: errorGetRatings,
  } = useGetOne(resource, { id, meta: metaRatings });

  const {
    data: providerReviews,
    isLoading: isLoadingReviews,
    refetch: refetchReviews,
    error: errorGetReviews,
  } = useGetOne(resource, { id, meta: metaReviews });

  const [deleteOne] = useDelete();

  const getReviewForRating = (clientId) => {
    const review = providerReviews.reviews?.find(
      (r) => r.clientId === clientId,
    );
    return review ?? {};
  };

  const refetch = () => {
    refetchRatings();
    refetchReviews();
  };

  const handleDeleteRating = (ratingId) => {
    deleteOne(
      "Rating",
      { id: ratingId, meta: { sparseFields: ["id"] } },
      {
        onSuccess: () => refetch(),
      },
    );
  };

  const handleDeleteReview = (reviewId) => {
    deleteOne(
      "Review",
      { id: reviewId, meta: { sparseFields: ["id"] } },
      {
        onSuccess: () => refetch(),
      },
    );
  };

  if (isLoadingRatings || isLoadingReviews) {
    return <div>Loading...</div>;
  }

  if (errorGetRatings) {
    throw errorGetRatings;
  }

  if (errorGetReviews) {
    throw errorGetReviews;
  }

  return (
    <Labeled sx={{ width: "100%" }} label="Ratings">
      <Stack sx={{ gap: "12px" }}>
        <Table>
          <TableBody>
            {(providerRatings.ratings || [])
              .filter((item) => item.clientId !== "1" && item.clientId !== 1)
              .map((item, index) => (
                <TableRow key={`rating-review-${index}`}>
                  <TableCell sx={{ width: "auto", paddingLeft: 0 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: "8px 32px",
                        gridTemplateColumns: "auto auto 1fr auto",
                      }}
                    >
                      <Labeled label="Customer ID">
                        <FunctionField render={() => item.clientId} />
                      </Labeled>

                      <Labeled label="Customer Name">
                        <FunctionField
                          render={() => {
                            return Number.isFinite(parseInt(item.clientId)) ? (
                              <CustomerNameField clientId={item.clientId} />
                            ) : (
                              <span>{item.clientUserName || "-"}</span>
                            );
                          }}
                        />
                      </Labeled>

                      <Labeled label="Added on">
                        <FunctionField
                          render={() =>
                            item["createdTimestamp"]
                              ? new Date(
                                  +item["createdTimestamp"],
                                ).toDateString()
                              : "-"
                          }
                        />
                      </Labeled>

                      <Labeled
                        label="Rating"
                        component="div"
                        sx={{ gridColumn: "span 3" }}
                      >
                        <FunctionField
                          render={() => (
                            <Box
                              sx={{
                                width: 200,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Rating
                                name="half-rating"
                                value={parseFloat(item.rating)}
                                precision={0.5}
                                readOnly
                              />
                              <Box sx={{ ml: 2 }}>{`${item.rating}/5`}</Box>
                            </Box>
                          )}
                        />
                      </Labeled>
                      <Labeled
                        label="Review"
                        component="div"
                        sx={{ gridColumn: "span 3" }}
                      >
                        <FunctionField
                          render={() => {
                            const review = getReviewForRating(item.clientId);
                            return review?.review || "-";
                          }}
                        />
                      </Labeled>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: "30px" }}>
                    <FunctionField
                      sx={{
                        gridRow: "1 / 3",
                        gridColumn: "4 / 5",
                        alignSelf: "center",
                      }}
                      render={() => (
                        <>
                          <IconButton
                            aria-label="delete"
                            onClick={() => setDeleteDialogOpenFor(item.id)}
                          >
                            <DeleteIcon />
                          </IconButton>

                          <Confirm
                            isOpen={deleteDialogOpenFor === item.id}
                            key={item.id}
                            title={`Delete item`}
                            content={`Are you sure you want to delete this item?`}
                            onConfirm={() => {
                              const review = getReviewForRating(item.clientId);
                              handleDeleteRating(item.id);
                              handleDeleteReview(review?.id);
                              setDeleteDialogOpenFor(null);
                            }}
                            onClose={() => setDeleteDialogOpenFor(null)}
                          />
                        </>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Stack>
    </Labeled>
  );
};

export default EditRatingsReviews;
